import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    Grid,
    Chip,
    Divider,
    useTheme,
    Paper,
    List,
    ListItem,
    ListItemText,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import {
    ArrowBack,
    Assessment,
    VideoLibrary,
    Person,
    Business,
    Schedule,
    FileUpload,
    CheckCircle,
    ExpandMore,
} from '@mui/icons-material';
import { useUser } from '../../contexts/UserContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { getPresentation } from '../../utils/api/presentationApi';
import { getConsultants } from '../../utils/api/consultantApi';
import {
    getStatusLabel,
    getStatusColor,
} from '../../utils/api/evaluationPipelineApi';
// Add Firestore imports for real-time updates
import { doc, onSnapshot, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseClient';
import { getPresentationReport, getSpeakerAnalysis } from '../../utils/api/reportingApi';

// Define local interfaces
interface Presentation {
    id: string;
    description: string;
    consultantId: string;
    userId: string;
    presenterName?: string;
    productName?: string;
    language?: string;
    presentationType?: string;
    evaluationStatus?: string;
    status?: string;
    overallScore?: number;
    structureScore?: number;
    softSkillsScore?: number;
    knowledgeScore?: number;
    createdAt: any;
    fileSize?: number;
    finalReport?: any;
    speakerLabels?: any[];
    transcription_text?: string;
    // Add other fields as needed
}

interface SpeakerAnalysis {
    speakerRoles: Record<string, string>;
    speakerConfidence: Record<string, number>;
    speakerReasoning: Record<string, string>;
    labeledSegments: any[];
    analysisNotes: string;
    keyIndicators: Record<string, any>;
    method: string;
    summary: {
        text: string;
        totalSpeakers: number;
        presenterCount: number;
        clientCount: number;
        overallConfidence: number;
        analysisMethod: string;
    };
    transcriptInfo: any;
    speakerStats: any;
    audioMetrics?: {
        wpm: number;
        fillerCount: number;
        fillerWords: Record<string, number>;
        sentiment: {
            positivePercent: number;
            negativePercent: number;
            neutralPercent: number;
            dominantSentiment: string;
        };
        durationSeconds: number;
    };
}

const PresentationDetails: React.FC = () => {
    const { user, userProfile } = useUser();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const theme = useTheme();

    const [presentation, setPresentation] = useState<Presentation | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [consultantLookup, setConsultantLookup] = useState<Record<string, any>>({});
    const [consultantsLoaded, setConsultantsLoaded] = useState(false);

    // Helper function to create lookup with current user info
    const createUserLookup = useCallback(() => {
        const lookup: Record<string, any> = {};

        if (user && userProfile) {
            const userName = user.displayName ||
                (userProfile.firstName && userProfile.lastName
                    ? `${userProfile.firstName} ${userProfile.lastName}`
                    : user.email) || 'Current User';

            lookup[user.uid] = {
                fullName: userName,
                company: userProfile.company || '',
                email: user.email || ''
            };
        }

        setConsultantLookup(lookup);
        setConsultantsLoaded(true);
    }, [user, userProfile]);

    const loadConsultants = useCallback(async () => {
        try {
            const result = await getConsultants({}) as { success: boolean; consultants?: any[]; error?: string };

            if (result.success && Array.isArray(result.consultants)) {
                const consultantList = result.consultants;

                // Create lookup map for fast consultant resolution
                const lookup: Record<string, any> = {};
                consultantList.forEach((consultant: any) => {
                    lookup[consultant.id] = {
                        fullName: `${consultant.firstName} ${consultant.lastName}`,
                        company: consultant.company || '',
                        email: consultant.email || ''
                    };
                });

                // Add current user to lookup as well
                if (user && userProfile) {
                    const userName = user.displayName ||
                        (userProfile.firstName && userProfile.lastName
                            ? `${userProfile.firstName} ${userProfile.lastName}`
                            : user.email) || 'Current User';

                    lookup[user.uid] = {
                        fullName: userName,
                        company: userProfile.company || '',
                        email: user.email || ''
                    };
                }

                setConsultantLookup(lookup);
                setConsultantsLoaded(true);
            } else {
                console.warn('Failed to load consultants:', result.error);
                // Still create lookup with current user info even if consultants fail to load
                createUserLookup();
            }
        } catch (error: any) {
            console.error('Error loading consultants:', error);
            // Handle index errors gracefully - consultants might not be available yet
            if (error.message && error.message.includes('index')) {
                console.log('Consultant indexes still building, will show user names where available');
            }
            createUserLookup();
        }
    }, [createUserLookup, user, userProfile]);

    // Helper function to get presenter information
    const getPresenterInfo = (presentation: Presentation) => {
        // Debug logging to trace the issue
        console.log('🔍 DETAIL DEBUG getPresenterInfo:', {
            presentationId: presentation.id,
            consultantId: presentation.consultantId,
            userId: presentation.userId,
            consultantsLoaded,
            consultantLookupKeys: Object.keys(consultantLookup),
            presentation: {
                description: presentation.description,
                consultantId: presentation.consultantId,
                userId: presentation.userId
            }
        });

        // If consultants haven't finished loading, show loading state
        if (!consultantsLoaded) {
            console.log('📋 DETAIL: Consultants not loaded yet, showing loading...');
            return {
                fullName: 'Loading...',
                company: '',
                email: ''
            };
        }

        // Check if we have a consultantId and it's different from userId (indicating coach mode)
        if (presentation.consultantId &&
            presentation.consultantId !== presentation.userId &&
            consultantLookup[presentation.consultantId]) {
            console.log('✅ DETAIL: Found external consultant:', presentation.consultantId, consultantLookup[presentation.consultantId]);
            return consultantLookup[presentation.consultantId];
        }

        // Debug: Check if consultantId exists but not found in lookup
        if (presentation.consultantId &&
            presentation.consultantId !== presentation.userId &&
            !consultantLookup[presentation.consultantId]) {
            console.log('⚠️ DETAIL: External consultant not found in lookup:', {
                consultantId: presentation.consultantId,
                availableKeys: Object.keys(consultantLookup)
            });
        }

        // Check if we have a consultantId that matches userId (regular mode or self-presentation)  
        if (presentation.consultantId &&
            presentation.consultantId === presentation.userId &&
            consultantLookup[presentation.consultantId]) {
            console.log('👤 DETAIL: Using self-presentation:', presentation.consultantId, consultantLookup[presentation.consultantId]);
            return consultantLookup[presentation.consultantId];
        }

        // Fallback: Use userId if we have it in our lookup
        if (presentation.userId && consultantLookup[presentation.userId]) {
            console.log('🔄 DETAIL: Using userId fallback:', presentation.userId, consultantLookup[presentation.userId]);
            return consultantLookup[presentation.userId];
        }

        // Final fallback for unknown consultants or missing data
        console.log('❌ DETAIL: No presenter info found, using final fallback');
        return {
            fullName: presentation.presenterName || 'Unknown Presenter',
            company: '',
            email: ''
        };
    };

    useDocumentTitle(presentation ? `Presentation: ${presentation.description}` : 'Presentation Details');

    // Step details functionality removed for simplified UI

    const fetchPresenterDetails = async () => {
        if (!presentation) return { error: 'No presentation loaded' };

        try {
            // Call Cloud Function to get comprehensive speaker analysis
            const result = await getSpeakerAnalysis(presentation.id) as {
                success: boolean;
                speakerAnalysis?: any;
                summary?: any;
                transcriptInfo?: any;
                error?: string
            };

            if (result.success) {
                const analysis = result.speakerAnalysis;
                const summary = result.summary;
                const transcriptInfo = result.transcriptInfo;

                // Generate detailed summary
                const summaryText = `${summary.analysisMethod === 'openai_enhanced' ? 'AI-powered' : 'Heuristic'} analysis identified ${summary.totalSpeakers} speaker(s): ${summary.presenterCount} presenter(s) and ${summary.clientCount} client(s) with ${Math.round(summary.overallConfidence * 100)}% confidence.`;

                return {
                    speakerRoles: analysis.speakerRoles || {},
                    speakerConfidence: analysis.speakerConfidence || {},
                    speakerReasoning: analysis.speakerReasoning || {},
                    labeledSegments: analysis.labeledSegments || [],
                    analysisNotes: (analysis.detailed_feedback ? `${analysis.detailed_feedback}\n\n` : '') + (analysis.analysisNotes || ''),
                    keyIndicators: analysis.keyIndicators || {},
                    method: analysis.method || 'unknown',
                    summary: {
                        text: summaryText,
                        totalSpeakers: summary.totalSpeakers,
                        presenterCount: summary.presenterCount,
                        clientCount: summary.clientCount,
                        overallConfidence: summary.overallConfidence,
                        analysisMethod: summary.analysisMethod
                    },
                    transcriptInfo: transcriptInfo,
                    speakerStats: analysis.speakerStats || {},
                    audioMetrics: analysis.audioMetrics || null
                };
            } else {
                // Fallback to basic information if speaker analysis not available
                const speakerLabels = presentation.speakerLabels || [];
                const transcription = presentation.transcription_text || '';

                return {
                    error: result.error || 'Speaker analysis not available',
                    fallback: {
                        speakerLabels,
                        transcription,
                        summary: speakerLabels.length > 0
                            ? `Basic detection found ${speakerLabels.length} speaker(s) - detailed analysis pending`
                            : 'Speaker identification pending or no speakers detected'
                    }
                };
            }
        } catch (error) {
            console.error('Error fetching speaker analysis:', error);
            return {
                error: 'Failed to load speaker analysis',
                fallback: {
                    summary: 'Unable to retrieve speaker analysis data'
                }
            };
        }
    };

    const fetchCriterionResults = async (category: string) => {
        if (!presentation || !userProfile) return [];

        try {
            const criterionQuery = query(
                collection(db, 'criterionResults'),
                where('presentationId', '==', presentation.id),
                where('tenantId', '==', userProfile.tenantId),
                where('category', '==', category)
            );

            const snapshot = await getDocs(criterionQuery);
            const results: any[] = [];
            snapshot.forEach((doc) => {
                results.push({ id: doc.id, ...doc.data() });
            });

            return results.sort((a, b) => {
                const dateA = a.evaluatedAt ? new Date(a.evaluatedAt.seconds * 1000) : new Date(0);
                const dateB = b.evaluatedAt ? new Date(b.evaluatedAt.seconds * 1000) : new Date(0);
                return dateB.getTime() - dateA.getTime();
            });
        } catch (error) {
            console.error('Error fetching criterion results:', error);
            return [];
        }
    };

    const fetchGeneratedReport = async () => {
        if (!presentation) return { error: 'No presentation loaded' };

        try {
            const result = await getPresentationReport(presentation.id) as {
                success: boolean;
                report?: {
                    markdownContent?: string;
                    overallScore?: number;
                    generatedAt?: any
                };
                error?: string
            };
            if (result.success && result.report) {
                return {
                    markdownContent: result.report.markdownContent,
                    overallScore: result.report.overallScore,
                    generatedAt: result.report.generatedAt
                };
            }
            return { error: 'Report not available' };
        } catch (error) {
            console.error('Error fetching report:', error);
            return { error: 'Failed to load report' };
        }
    };

    // Content rendering functions for different data types
    const renderStepContent = (data: any) => {
        if (!data) return <Typography>Loading...</Typography>;

        switch (data.type) {
            case 'transcription':
                return (
                    <Box>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Full presentation transcription:
                        </Typography>
                        <Paper sx={{ p: 2, maxHeight: 400, overflow: 'auto', backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50] }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                                {data.content || 'No transcription available'}
                            </Typography>
                        </Paper>
                    </Box>
                );

            case 'presenter':
                return (
                    <Box>
                        {/* Handle error state with fallback */}
                        {data.content.error ? (
                            <Box>
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    {data.content.error}
                                </Alert>
                                {data.content.fallback && (
                                    <Box>
                                        <Typography variant="body1" sx={{ mb: 2 }}>
                                            {data.content.fallback.summary}
                                        </Typography>
                                        {data.content.fallback.speakerLabels && data.content.fallback.speakerLabels.length > 0 && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="h6" sx={{ mb: 1 }}>Basic Speaker Detection:</Typography>
                                                {data.content.fallback.speakerLabels.map((speaker: string, index: number) => (
                                                    <Chip key={index} label={`Speaker ${index + 1}`} sx={{ mr: 1, mb: 1 }} />
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            <Box>
                                {/* Summary */}
                                <Typography variant="body1" sx={{ mb: 2, fontWeight: 'medium' }}>
                                    {data.content.summary.text}
                                </Typography>

                                {/* Analysis Method Badge */}
                                <Box sx={{ mb: 3 }}>
                                    <Chip
                                        label={`Analysis Method: ${data.content.summary.analysisMethod === 'openai_enhanced' ? 'AI-Powered' : 'Heuristic'}`}
                                        color={data.content.summary.analysisMethod === 'openai_enhanced' ? 'primary' : 'default'}
                                        variant="outlined"
                                        sx={{ mr: 1 }}
                                    />
                                    <Chip
                                        label={`Confidence: ${Math.round(data.content.summary.overallConfidence * 100)}%`}
                                        color={data.content.summary.overallConfidence > 0.8 ? 'success' : data.content.summary.overallConfidence > 0.6 ? 'warning' : 'error'}
                                        variant="outlined"
                                    />
                                </Box>

                                {/* Audio Metrics Display */}
                                {/* Audio Metrics Display */}
                                {(data.content as SpeakerAnalysis).audioMetrics && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="h6" sx={{ mb: 2 }}>Delivery & Presence (Non-Verbal)</Typography>
                                        <Paper sx={{ p: 2, mb: 2, backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50] }}>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography variant="subtitle2" color="text.secondary">Speaking Pace</Typography>
                                                    <Typography variant="h4" color="primary">{(data.content as SpeakerAnalysis).audioMetrics?.wpm || 0}</Typography>
                                                    <Typography variant="caption">WPM (Target: 130-150)</Typography>
                                                </Box>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography variant="subtitle2" color="text.secondary">Filler Words</Typography>
                                                    <Typography variant="h4" color={((data.content as SpeakerAnalysis).audioMetrics?.fillerCount || 0) > 10 ? 'error' : 'success'}>
                                                        {(data.content as SpeakerAnalysis).audioMetrics?.fillerCount || 0}
                                                    </Typography>
                                                    <Typography variant="caption">Total Detected</Typography>
                                                </Box>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Typography variant="subtitle2" color="text.secondary">Dominant Tone</Typography>
                                                    <Typography variant="h4" color="info.main">
                                                        {(data.content as SpeakerAnalysis).audioMetrics?.sentiment?.dominantSentiment || 'Neutral'}
                                                    </Typography>
                                                    <Typography variant="caption">
                                                        {(data.content as SpeakerAnalysis).audioMetrics?.sentiment?.positivePercent}% Positive
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Box>
                                )}

                                {/* Speaker Roles */}
                                {Object.keys(data.content.speakerRoles).length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="h6" sx={{ mb: 2 }}>Speaker Role Analysis</Typography>
                                        <Grid container spacing={2}>
                                            {Object.entries(data.content.speakerRoles).map(([speakerId, role]) => {
                                                const confidence = data.content.speakerConfidence[speakerId] || 0;
                                                const reasoning = data.content.speakerReasoning[speakerId] || '';

                                                return (
                                                    <Grid size={{ xs: 12, sm: 6 }} key={speakerId}>
                                                        <Paper sx={{ p: 2, backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50] }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                                                                    Speaker {speakerId}
                                                                </Typography>
                                                                <Chip
                                                                    label={role as string}
                                                                    color={role === 'Presenter' ? 'primary' : 'secondary'}
                                                                    size="small"
                                                                />
                                                            </Box>
                                                            <Box sx={{ mb: 1 }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Confidence: {Math.round((confidence as number) * 100)}%
                                                                </Typography>
                                                            </Box>
                                                            {reasoning && (
                                                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                                                    {(reasoning as string).length > 100 ? `${(reasoning as string).substring(0, 100)}...` : reasoning}
                                                                </Typography>
                                                            )}
                                                        </Paper>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </Box>
                                )}

                                {/* Key Indicators */}
                                {data.content.keyIndicators && Object.keys(data.content.keyIndicators).length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="h6" sx={{ mb: 2 }}>Key Analysis Indicators</Typography>
                                        <Paper sx={{ p: 2, backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50] }}>
                                            <Grid container spacing={2}>
                                                {data.content.keyIndicators.first_speaker && (
                                                    <Grid size={{ xs: 12, sm: 6 }}>
                                                        <Typography variant="body2">
                                                            <strong>First Speaker:</strong> {data.content.keyIndicators.first_speaker}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                                {data.content.keyIndicators.primary_presenter && (
                                                    <Grid size={{ xs: 12, sm: 6 }}>
                                                        <Typography variant="body2">
                                                            <strong>Primary Presenter:</strong> {data.content.keyIndicators.primary_presenter}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                                {data.content.keyIndicators.content_leader && (
                                                    <Grid size={{ xs: 12, sm: 6 }}>
                                                        <Typography variant="body2">
                                                            <strong>Content Leader:</strong> {data.content.keyIndicators.content_leader}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                                {data.content.keyIndicators.question_askers && data.content.keyIndicators.question_askers.length > 0 && (
                                                    <Grid size={{ xs: 12, sm: 6 }}>
                                                        <Typography variant="body2">
                                                            <strong>Question Askers:</strong> {data.content.keyIndicators.question_askers.join(', ')}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Paper>
                                    </Box>
                                )}

                                {/* Analysis Notes */}
                                {data.content.analysisNotes && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="h6" sx={{ mb: 1 }}>Analysis Notes</Typography>
                                        <Paper sx={{ p: 2, backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50] }}>
                                            <Typography variant="body2">
                                                {data.content.analysisNotes}
                                            </Typography>
                                        </Paper>
                                    </Box>
                                )}

                                {/* Sample Labeled Segments */}
                                {data.content.labeledSegments && data.content.labeledSegments.length > 0 && (
                                    <Box>
                                        <Typography variant="h6" sx={{ mb: 2 }}>Sample Transcript with Speaker Labels</Typography>
                                        <Paper sx={{ p: 2, maxHeight: 400, overflow: 'auto', backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50] }}>
                                            {data.content.labeledSegments.slice(0, 10).map((segment: any, index: number) => (
                                                <Box key={index} sx={{ mb: 1, borderLeft: `3px solid ${segment.speakerRole === 'Presenter' ? theme.palette.primary.main : theme.palette.secondary.main}`, pl: 2 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                        <Typography variant="caption" sx={{ fontWeight: 'bold', mr: 1 }}>
                                                            Speaker {segment.speaker}
                                                        </Typography>
                                                        <Chip
                                                            label={segment.speakerRole || 'Unknown'}
                                                            size="small"
                                                            color={segment.speakerRole === 'Presenter' ? 'primary' : 'secondary'}
                                                            sx={{ height: 18, fontSize: '0.7rem' }}
                                                        />
                                                        {segment.roleConfidence && (
                                                            <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                                                ({Math.round(segment.roleConfidence * 100)}%)
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                        {segment.text}
                                                    </Typography>
                                                </Box>
                                            ))}
                                            {data.content.labeledSegments.length > 10 && (
                                                <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                                    ... and {data.content.labeledSegments.length - 10} more segments
                                                </Typography>
                                            )}
                                        </Paper>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                );

            case 'criteria':
                return (
                    <Box>
                        {data.content.length === 0 ? (
                            <Typography>No evaluation results available for this category yet.</Typography>
                        ) : (
                            data.content.map((criterion: any, index: number) => (
                                <Accordion key={criterion.id || index} sx={{ mb: 1 }}>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                                                {criterion.criterionName}
                                            </Typography>
                                            <Chip
                                                label={`${criterion.score}/5`}
                                                color={criterion.score >= 4 ? 'success' : criterion.score >= 3 ? 'warning' : 'error'}
                                                size="small"
                                            />
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box>
                                            <Typography variant="body2" sx={{ mb: 2 }}>
                                                <strong>Feedback:</strong> {criterion.feedback}
                                            </Typography>
                                            {criterion.recommendations && criterion.recommendations.length > 0 && (
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                        Recommendations:
                                                    </Typography>
                                                    <List dense>
                                                        {criterion.recommendations.map((rec: string, idx: number) => (
                                                            <ListItem key={idx} sx={{ py: 0 }}>
                                                                <ListItemText primary={`• ${rec}`} />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </Box>
                                            )}
                                            {criterion.strengths && criterion.strengths.length > 0 && (
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'success.main' }}>
                                                        Strengths:
                                                    </Typography>
                                                    <List dense>
                                                        {criterion.strengths.map((strength: string, idx: number) => (
                                                            <ListItem key={idx} sx={{ py: 0 }}>
                                                                <ListItemText primary={`✓ ${strength}`} />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </Box>
                                            )}
                                            {criterion.areasForImprovement && criterion.areasForImprovement.length > 0 && (
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'warning.main' }}>
                                                        Areas for Improvement:
                                                    </Typography>
                                                    <List dense>
                                                        {criterion.areasForImprovement.map((area: string, idx: number) => (
                                                            <ListItem key={idx} sx={{ py: 0 }}>
                                                                <ListItemText primary={`⚠ ${area}`} />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </Box>
                                            )}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            ))
                        )}
                    </Box>
                );

            case 'report':
                return (
                    <Box>
                        {data.content.error ? (
                            <Typography color="error">{data.content.error}</Typography>
                        ) : (
                            <Box>
                                {data.content.overallScore && (
                                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                                        <Typography variant="h4" color="primary">
                                            Overall Score: {data.content.overallScore.toFixed(1)}/5.0
                                        </Typography>
                                    </Box>
                                )}
                                <Paper sx={{ p: 2, maxHeight: 500, overflow: 'auto', backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50] }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {data.content.markdownContent || 'Report content not available'}
                                    </Typography>
                                </Paper>
                                {data.content.generatedAt && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                        Generated: {new Date(data.content.generatedAt.seconds * 1000).toLocaleString()}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                );

            default:
                return <Typography>No data available</Typography>;
        }
    };

    const loadPresentation = async () => {
        if (!id) return;

        try {
            setLoading(true);
            setError(null);

            const result = await getPresentation(id) as {
                success: boolean;
                presentation?: any;
                error?: string
            };

            if (result.success) {
                setPresentation(result.presentation);

                // Real-time updates will handle status changes automatically
            } else {
                setError(result.error || 'Failed to load presentation');
            }
        } catch (err: any) {
            console.error('Error loading presentation:', err);
            setError(err.message || 'Failed to load presentation');
        } finally {
            setLoading(false);
        }
    };


    const formatDate = (timestamp: any) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
        return date.toLocaleString();
    };

    const formatFileSize = (bytes?: number) => {
        if (!bytes) return 'N/A';
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
    };

    useEffect(() => {
        loadPresentation();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load consultants when user data is available
    useEffect(() => {
        if (user && userProfile) {
            loadConsultants();
        }
    }, [user, userProfile, loadConsultants]);

    // Real-time status updates using Firestore listener
    useEffect(() => {
        if (!presentation?.id) return;

        // Set up real-time listener for presentation status
        const presentationRef = doc(db, 'presentations', presentation.id);
        const unsubscribe = onSnapshot(presentationRef, (doc) => {
            if (doc.exists()) {
                const updatedData = doc.data() as Presentation;

                // Don't override evaluationStatus with upload status
                // status = upload status ('uploaded', 'processing', etc.)
                // evaluationStatus = pipeline status ('NEW', 'PROCESSING_TRANSCRIPTION', 'COMPLETE', etc.)
                // Keep them separate and ensure evaluationStatus defaults to 'NEW' if not set
                if (!updatedData.evaluationStatus && updatedData.status === 'uploaded') {
                    updatedData.evaluationStatus = 'NEW';
                }

                setPresentation(prev => ({
                    ...prev!,
                    ...updatedData,
                    id: doc.id
                }));
            }
        }, (error) => {
            console.error('❌ Error listening to presentation updates:', error);
        });

        // Cleanup listener on unmount
        return () => {
            unsubscribe();
        };
    }, [presentation?.id]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/presentations')}
                    >
                        Back to Presentations
                    </Button>
                </Box>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button variant="contained" onClick={loadPresentation}>
                    Retry
                </Button>
            </Box>
        );
    }

    if (!presentation) {
        return (
            <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/presentations')}
                    >
                        Back to Presentations
                    </Button>
                </Box>
                <Alert severity="warning">
                    Presentation not found
                </Alert>
            </Box>
        );
    }

    const status = presentation.evaluationStatus || presentation.status || 'NEW';
    const isComplete = status === 'COMPLETE' || status === 'COMPLETE_V2' || status === 'Completed' || status === 'complete';

    return (
        <Box sx={{ flexGrow: 1 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate('/presentations')}
                >
                    Back to Presentations
                </Button>
                <Typography variant="h4" sx={{ flexGrow: 1 }}>
                    {presentation.description}
                </Typography>

                {/* View Report Button - Prominent when complete */}
                {isComplete && (
                    <Button
                        variant="contained"
                        startIcon={<Assessment />}
                        onClick={() => navigate(`/presentations/${presentation.id}/report`)}
                        color="primary"
                        size="large"
                        sx={{ minWidth: 180 }}
                    >
                        View Report
                    </Button>
                )}
            </Box>

            {/* Status Message */}
            {isComplete && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CheckCircle />
                        <Box>
                            <Typography variant="body1" fontWeight="medium">
                                Evaluation Complete
                            </Typography>
                            <Typography variant="body2">
                                Your presentation has been successfully analyzed. Click "View Report" to see detailed results.
                            </Typography>
                        </Box>
                    </Box>
                </Alert>
            )}

            {(status === 'FAILED' || status === 'FAILED_V2') && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    <Typography variant="body1">
                        The evaluation encountered an error. Please contact support if you need assistance.
                    </Typography>
                </Alert>
            )}

            {/* Simplified Main Information Card */}
            <Card>
                <CardContent sx={{ p: 4 }}>
                    {/* Presentation Overview */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 4 }}>
                        <VideoLibrary sx={{ color: 'primary.main', fontSize: 48, mt: 1 }} />
                        <Box sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                                <Chip
                                    label={presentation.presentationType || 'Presentation'}
                                    color="primary"
                                    variant="outlined"
                                />
                                <Chip
                                    label={getStatusLabel(status)}
                                    color={getStatusColor(status) as any}
                                    variant={isComplete ? 'filled' : 'outlined'}
                                />
                            </Box>

                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
                                A comprehensive evaluation that analyzes presentation structure, speaker skills, and content knowledge to provide detailed feedback and recommendations.
                            </Typography>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Key Information - Simplified Grid */}
                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Business sx={{ color: 'primary.main', fontSize: 32, mb: 1 }} />
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Product
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {presentation.productName || 'Not specified'}
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Person sx={{ color: 'primary.main', fontSize: 32, mb: 1 }} />
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Presenter
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {getPresenterInfo(presentation).fullName}
                                </Typography>
                                {getPresenterInfo(presentation).company && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {getPresenterInfo(presentation).company}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Schedule sx={{ color: 'primary.main', fontSize: 32, mb: 1 }} />
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Upload Date
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {formatDate(presentation.createdAt)}
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <FileUpload sx={{ color: 'primary.main', fontSize: 32, mb: 1 }} />
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    File Size
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {formatFileSize(presentation.fileSize)}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

        </Box>
    );
};

export default PresentationDetails;
